import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Target, Layers, ImageIcon, ChevronRight } from 'lucide-react';
import { FbWorkspaceProvider, useFbWorkspace, type WorkspacePlatform, type RegistryAppPromotion } from '@/contexts/FbWorkspaceContext';
import { marketingAppsService } from '@/services/marketing-apps.service';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { facebookMarketingActionsService } from '@/services/facebook-marketing-actions.service';
import { facebookAdAccountService, MetaAdAccount } from '@/services/facebook-ad-account.service';
import { WorkspaceSidebar } from '@/components/facebook/workspace/WorkspaceSidebar';
import { CampaignFormPanel } from '@/components/facebook/workspace/CampaignFormPanel';
import { AdFormPanel } from '@/components/facebook/workspace/AdFormPanel';
import { ActivateDialog } from '@/components/facebook/workspace/ActivateDialog';
import { ContextHelpPanel } from '@/components/facebook/workspace/ContextHelpPanel';
import { AccountHealthBanner } from '@/components/facebook/AccountHealthBanner';

// Per-objective adset forms
import { TrafficAdSetForm }       from '@/components/facebook/workspace/adset/TrafficAdSetForm';
import { LeadsAdSetForm }         from '@/components/facebook/workspace/adset/LeadsAdSetForm';
import { EngagementAdSetForm }    from '@/components/facebook/workspace/adset/EngagementAdSetForm';
import { AwarenessAdSetForm }     from '@/components/facebook/workspace/adset/AwarenessAdSetForm';
import { SalesAdSetForm }         from '@/components/facebook/workspace/adset/SalesAdSetForm';
import { AppPromotionsAdSetForm } from '@/components/facebook/workspace/adset/AppPromotionsAdSetForm';

// ─── Objective → form mapping ─────────────────────────────────────────────────

function AdSetFormByObjective({ objective }: { objective: string }) {
  switch (objective) {
    case 'OUTCOME_TRAFFIC':       return <TrafficAdSetForm />;
    case 'OUTCOME_LEADS':         return <LeadsAdSetForm />;
    case 'OUTCOME_ENGAGEMENT':    return <EngagementAdSetForm />;
    case 'OUTCOME_AWARENESS':     return <AwarenessAdSetForm />;
    case 'OUTCOME_SALES':         return <SalesAdSetForm />;
    case 'OUTCOME_APP_PROMOTION': return <AppPromotionsAdSetForm />;
    default:
      return (
        <div className="rounded-2xl border border-border bg-surface-alt/40 p-8 text-center text-sm text-text-muted">
          Select an objective in the campaign tab first.
        </div>
      );
  }
}

// ─── Breadcrumb ───────────────────────────────────────────────────────────────

function Breadcrumb() {
  const { campaign, activeLevel, activeAdSet, activeAd, setActiveLevel, activeAdSetId } = useFbWorkspace();

  return (
    <div className="flex items-center gap-1 text-xs text-text-muted">
      <button
        type="button"
        onClick={() => setActiveLevel('campaign', activeAdSetId, null)}
        className="flex items-center gap-1 hover:text-text-primary transition-colors"
      >
        <Target size={12} />
        {campaign.name || 'Campaign'}
      </button>

      {activeLevel !== 'campaign' && activeAdSet && (
        <>
          <ChevronRight size={10} />
          <button
            type="button"
            onClick={() => setActiveLevel('adset', activeAdSetId, null)}
            className="flex items-center gap-1 hover:text-text-primary transition-colors"
          >
            <Layers size={12} />
            {activeAdSet.name}
          </button>
        </>
      )}

      {activeLevel === 'ad' && activeAd && (
        <>
          <ChevronRight size={10} />
          <span className="flex items-center gap-1 text-text-primary">
            <ImageIcon size={12} />
            {activeAd.name}
          </span>
        </>
      )}
    </div>
  );
}

// ─── Active panel title ───────────────────────────────────────────────────────

const LEVEL_LABELS: Record<string, string> = {
  campaign: 'Campaign setup',
  adset:    'Ad Set configuration',
  ad:       'Ad creative',
};

// ─── WorkspaceInner ───────────────────────────────────────────────────────────

function WorkspaceInner() {
  const navigate = useNavigate();
  const { campaign, adSets, activeLevel, activeAdSetId, activeAdId, reset, platform, appPromotion } = useFbWorkspace();
  const [showActivate, setShowActivate] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [account, setAccount] = useState<MetaAdAccount | null>(null);
  // Which list view to send the user to after activation. The IG workspace
  // takes them back to Instagram → Campaigns (where their newly-launched
  // campaign appears with a Cross-platform badge if they happened to also
  // pick FB placements — but in IG-locked mode that's impossible).
  const postActivateListPath = platform === 'instagram' ? '/instagram/campaigns' : '/facebook/campaigns';

  useEffect(() => {
    facebookAdAccountService.get().then(setAccount).catch(() => undefined);
  }, []);

  const handleActivate = async () => {
    if (appPromotion) return;
    if (!campaign.apiId) { toast.error('Campaign not saved'); return; }
    setIsPublishing(true);
    try {
      const adSetIds = adSets.filter(a => a.apiId).map(a => a.apiId!);
      const adIds = adSets.flatMap(a => a.ads.filter(x => x.apiId).map(x => x.apiId!));

      await facebookMarketingActionsService.activateFullCampaign({
        campaign_id: campaign.apiId,
        adset_ids: adSetIds,
        ad_ids: adIds,
      });
      const activatedApiId = campaign.apiId;
      toast.success('Campaign activated!');
      setShowActivate(false);
      reset();
      // Campaign detail still lives under /facebook/campaigns/:id — both
      // FB and IG modules share the same detail page (cross-platform banner
      // shows on it when relevant). Only the LIST view differs by module.
      void postActivateListPath; // post-activate landing is the detail page; list path kept for future use
      navigate(`/facebook/campaigns/${activatedApiId}`);
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally {
      setIsPublishing(false);
    }
  };

  const totalAds = adSets.reduce((s, a) => s + a.ads.length, 0);

  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      {/* Sidebar */}
      <WorkspaceSidebar
        onActivate={() => setShowActivate(true)}
        isPublishing={isPublishing}
      />

      {/* Main workspace area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-surface shrink-0">
          <div className="space-y-0.5">
            <h2 className="text-sm font-semibold text-text-primary">{LEVEL_LABELS[activeLevel]}</h2>
            <Breadcrumb />
            {appPromotion && <p className="text-xs text-text-muted">{appPromotion.name} · {appPromotion.accountName} · Saves paused. Install attribution/tracking is not configured.</p>}
          </div>
        </div>

        {/* Panel area — form on left, contextual help on right (≥xl).
            Workspace forms render full-bleed within their column (no max-w cap)
            so wide screens use the available real estate. The right help panel
            is fixed-width and stays on the right. */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {/* Capped and centred — past ~1400px the inputs just stretch and
                the eye has to travel from label to field. */}
            <div className="mx-auto w-full max-w-[1400px] space-y-4">
              <AccountHealthBanner account={account} />
              {activeLevel === 'campaign' && <CampaignFormPanel />}
              {activeLevel === 'adset'   && (
                <AdSetFormByObjective
                  key={`adset-${activeAdSetId ?? 'none'}`}
                  objective={campaign.objective}
                />
              )}
              {activeLevel === 'ad'      && (
                <AdFormPanel
                  key={`ad-${activeAdId ?? 'none'}`}
                />
              )}
            </div>
          </div>
          <ContextHelpPanel />
        </div>
      </div>

      {/* Activate dialog */}
      {showActivate && (
        <ActivateDialog
          adSetCount={adSets.filter(a => a.apiId).length}
          adCount={totalAds}
          onConfirm={handleActivate}
          onCancel={() => setShowActivate(false)}
          isPublishing={isPublishing}
        />
      )}
    </div>
  );
}

// ─── FbWorkspace (page) ───────────────────────────────────────────────────────

/**
 * The same workspace shell powers both Facebook and Instagram modules.
 * `platform` is supplied by the route wrapper (App.tsx mounts
 * `<FbWorkspace platform="instagram" />` under /instagram/workspace) and
 * propagates through context — placements lock to IG, the ad form forces
 * the IG actor, and the post-activate navigation lands on the IG list.
 */
interface FbWorkspaceProps {
  platform?: WorkspacePlatform;
}

export default function FbWorkspace({ platform = 'facebook' }: FbWorkspaceProps) {
  const { draftId } = useParams<{ draftId?: string }>();
  const [params] = useSearchParams();
  const appUid = params.get('appUid');
  if (appUid) return <RegistryAppWorkspace key={`${platform}:${appUid}`} appUid={appUid} platform={platform} />;

  return (
    <FbWorkspaceProvider resumeDraftId={draftId} platform={platform}>
      <WorkspaceInner />
    </FbWorkspaceProvider>
  );
}

function RegistryAppWorkspace({ appUid, platform }: { appUid: string; platform: WorkspacePlatform }) {
  const [context, setContext] = useState<RegistryAppPromotion | null>(null);
  const [error, setError] = useState(''); const [confirmed, setConfirmed] = useState(false); const [revision, refresh] = useState(0);
  useEffect(() => {
    let current = true; setContext(null); setError(''); setConfirmed(false);
    Promise.all([marketingAppsService.get(appUid), facebookAdAccountService.get()]).then(([app, account]) => {
      if (!current) return;
      if (app.status !== 'active' || !app.readiness.meta.detailsReady || !app.metaAppId) throw new Error('Add the Meta app ID and store URL to an active app before promoting it.');
      if (!account?.accountUid || !account.isActive || !account.isDefault || account.companyUid !== app.companyUid) throw new Error('Choose an active default Meta ad account in Ad Accounts first.');
      setContext({ appUid: app.appUid, companyUid: app.companyUid, name: app.name, platform: app.platform, metaAppId: app.metaAppId,
        storeUrl: (app.platform === 'android' ? app.playStoreUrl : app.appStoreUrl)!, accountUid: account.accountUid, accountName: account.adAccountName || account.adAccountId, accountCurrency: account.currency });
    }).catch(e => { if (current) setError(e?.response ? 'Could not load the app or current Meta account. Check your app details and account access.' : e?.message || 'Could not open app promotion.'); });
    return () => { current = false; };
  }, [appUid, revision]);
  if (confirmed && context) return <FbWorkspaceProvider key={`${context.companyUid}:${context.appUid}:${context.accountUid}`} platform={platform} appPromotion={context}><WorkspaceInner /></FbWorkspaceProvider>;
  return <div className="space-y-4 p-5 md:p-6">
    <Link to={`/marketing/apps/${encodeURIComponent(appUid)}`} className="text-sm text-primary">← App details</Link>
    <Card className="max-w-3xl"><h1 className="text-xl font-semibold text-text-primary">Promote your app on Meta</h1>
      {error ? <p role="alert" className="mt-4 text-sm text-danger">{error}</p> : !context ? <p className="mt-4 text-sm text-text-muted">Loading app and advertising account…</p> : <>
        <div className="mt-4 grid gap-4 rounded-xl bg-surface-alt p-4 sm:grid-cols-2"><div><p className="text-xs text-text-muted">App</p><p className="mt-1 font-medium text-text-primary">{context.name} · {context.platform === 'ios' ? 'iOS' : 'Android'}</p></div><div><p className="text-xs text-text-muted">Selected ad account</p><p className="mt-1 font-medium text-text-primary">{context.accountName}</p></div></div>
        <p className="mt-4 text-sm text-text-secondary">Review your budget, audience and creative in the campaign builder. Everything saves paused.</p>
        <p className="mt-2 text-xs text-text-muted">Install attribution/tracking is not configured in this version. Meta app permissions and event requirements are checked by Meta when you save.</p>
      </>}
      <div className="mt-5 flex flex-wrap items-center gap-4">{context && <Button onClick={() => setConfirmed(true)}>Continue with this account</Button>}{error && <Button variant="secondary" onClick={() => refresh(value => value + 1)}>Retry</Button>}<Link className="text-sm text-primary" to="/facebook/accounts">Choose default ad account</Link></div>
    </Card>
  </div>;
}
